import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Image,
  Plus,
  Search,
  Trash2,
  X,
  Save,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const { token } = useAuth();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    caption: '',
    image_path: '',
    category: 'general',
    status: 'active'
  });

  const categories = ['general', 'facilities', 'events', 'staff', 'equipment', 'community'];

  const fetchGallery = async () => {
    try {
      const response = await fetch(`${API}/api/admin/gallery`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGallery(data);
      }
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/api/admin/gallery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Image added to gallery');
        setShowModal(false);
        resetForm();
        fetchGallery();
      } else {
        throw new Error('Operation failed');
      }
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/gallery/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Image deleted');
        fetchGallery();
      }
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      caption: '',
      image_path: '',
      category: 'general',
      status: 'active'
    });
  };

  const filteredGallery = gallery.filter(item =>
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
              <Image className="w-7 h-7 text-primary" />
              Gallery Management
            </h1>
            <p className="text-slate-600">Manage hospital photo gallery</p>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-primary hover:bg-primary-700" data-testid="add-gallery-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Image
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search gallery..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="gallery-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {loading ? (
            <div className="col-span-full p-8 text-center text-slate-500">Loading...</div>
          ) : filteredGallery.length === 0 ? (
            <div className="col-span-full p-8 text-center text-slate-500">No images found</div>
          ) : (
            filteredGallery.map((item) => (
              <Card key={item.id} className="overflow-hidden group">
                <div className="aspect-square bg-slate-100 relative">
                  {item.image_path ? (
                    <img src={item.image_path} alt={item.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setPreviewImage(item)}
                      data-testid={`preview-gallery-${item.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(item.id)}
                      data-testid={`delete-gallery-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-3">
                  <h3 className="font-medium text-sm text-slate-800 truncate">{item.title}</h3>
                  <span className="text-xs text-primary">{item.category}</span>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Add Image Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Add Image to Gallery</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      data-testid="gallery-title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="image_path">Image URL *</Label>
                    <Input
                      id="image_path"
                      value={formData.image_path}
                      onChange={(e) => setFormData({...formData, image_path: e.target.value})}
                      placeholder="https://..."
                      required
                      data-testid="gallery-image-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="caption">Caption</Label>
                    <textarea
                      id="caption"
                      value={formData.caption}
                      onChange={(e) => setFormData({...formData, caption: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      rows={2}
                      data-testid="gallery-caption-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <select
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        data-testid="gallery-category-select"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        data-testid="gallery-status-select"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" className="bg-primary hover:bg-primary-700" data-testid="gallery-save-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Add Image
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Preview Modal */}
        {previewImage && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewImage(null)}>
            <div className="max-w-4xl w-full">
              <img 
                src={previewImage.image_path} 
                alt={previewImage.title}
                className="w-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="text-center mt-4">
                <h3 className="text-white font-semibold">{previewImage.title}</h3>
                {previewImage.caption && (
                  <p className="text-white/70 text-sm mt-1">{previewImage.caption}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminGallery;
